import { unwrap } from "@sagot/api-client";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "../shared/engage-keys";
import { isRangeValid, toQueryParams, type AnalyticsFilters } from "./analytics-model";

const ANALYTICS_STALE_MS = 60_000;

function useAnalyticsQuery<T>(name: string, filters: AnalyticsFilters, fetcher: (query: ReturnType<typeof toQueryParams>) => Promise<T>) {
  const wsId = useWorkspaceId();
  const query = toQueryParams(filters);
  return useQuery({
    queryKey: [...engageKeys.analytics(wsId), name, query],
    queryFn: () => fetcher(query),
    enabled: isRangeValid(filters),
    staleTime: ANALYTICS_STALE_MS,
  });
}

export function useSummary(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("summary", filters, (query) => unwrap(api.GET("/v1/analytics/summary", { params: { query } })));
}

export function useTimeseries(filters: AnalyticsFilters, interval: "day" | "week") {
  const api = useApi();
  return useAnalyticsQuery(`timeseries-${interval}`, filters, (query) =>
    unwrap(api.GET("/v1/analytics/timeseries", { params: { query: { ...query, interval } } })),
  );
}

export function useChannelShares(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("channels", filters, (query) => unwrap(api.GET("/v1/analytics/channels", { params: { query } })));
}

export function useHeatmap(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("heatmap", filters, (query) => unwrap(api.GET("/v1/analytics/heatmap", { params: { query } })));
}

export function useTopics(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("topics", filters, (query) => unwrap(api.GET("/v1/analytics/topics", { params: { query } })));
}

export function useLanguages(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("languages", filters, (query) => unwrap(api.GET("/v1/analytics/languages", { params: { query } })));
}

export function useFeedback(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("feedback", filters, (query) => unwrap(api.GET("/v1/analytics/feedback", { params: { query } })));
}

export function useQuality(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("quality", filters, (query) => unwrap(api.GET("/v1/analytics/quality", { params: { query } })));
}

/** Workflow runs, completion, engagement and what they led to (`GET /v1/analytics/automation`). */
export function useAutomation(filters: AnalyticsFilters) {
  const api = useApi();
  return useAnalyticsQuery("automation", filters, (query) => unwrap(api.GET("/v1/analytics/automation", { params: { query } })));
}

/** `GET /v1/analytics/export?format=csv` as a Blob (+ server filename). */
export function useExportCsv() {
  const api = useApi();
  return async (filters: AnalyticsFilters): Promise<{ blob: Blob; filename: string | null }> => {
    const result = await api.GET("/v1/analytics/export", { params: { query: { ...toQueryParams(filters), format: "csv" } }, parseAs: "blob" });
    const blob = await unwrap(Promise.resolve(result));
    const disposition = result.response.headers.get("Content-Disposition") ?? "";
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? null;
    return { blob, filename };
  };
}
